const cloneDeep = require('lodash.clonedeep');
const {
    SECURE_SCORE_TYPE,
    REGULAR_SCORE_TYPE,
} = require('./calculation_constants');

const has = Object.prototype.hasOwnProperty;
const MEASURE_SCORES = 'measureScores';
const ORF_ACC = 'ORF-ACC';

const SECURE_ASSESSMENT_DATA_SOURCES = ['secure-assessment'];
const SUPPORTED_MEASURES = [
    'LNF',
    'PSF',
    'NWF',
    'ORF',
    'WRF',
    'MAZE',
];

/**
 * Get measureScores list from the result.
 *
 * @param {Object} result - Assessment result
 * @returns {Array} List of measure scores objects.
 */
function getScoresInfo(result) {
    if (has.call(result, MEASURE_SCORES)) {
        const resultMeasureScore = cloneDeep(result[MEASURE_SCORES]);
        return Object.entries(result[MEASURE_SCORES])
            .reduce((measureScores, [measure, scoreObj]) => ({
                ...measureScores,
                [measure]: {
                    ...scoreObj,
                    isDiscontinueRuleMet: result.isDiscontinueRuleMet || false,
                    serverCreateDate: result.serverCreateDate,
                    serverModDate: result.serverModDate,
                    clientCreateDate: result.clientCreateDate,
                    clientModDate: result.clientModDate,
                    attemptNumber: result.attemptNumber || null,
                },
            }
            ), resultMeasureScore);
    }
    return {
        [result.measure]: {
            rawScore: result.rawScore,
            interpretation: result.interpretation,
            isDiscontinueRuleMet: result.isDiscontinueRuleMet || false,
            serverCreateDate: result.serverCreateDate,
            serverModDate: result.serverModDate,
            clientCreateDate: result.clientCreateDate,
            clientModDate: result.clientModDate,
            attemptNumber: result.attemptNumber || null,
        },
    };
}

/**
 * Extract measure specific rawScore from given measureScore obj.
 *
 * Value of rawScore field is measure specific: ORF-ACC rawScore should be converted from percents to coef value for
 * further calculations. Other measures raw scores should be kept as they are.
 * @param {string} measureName
 * @param {Object} measureScoreObj
 * @returns {Number} measure specific raw score
 */
function getRawScore(measureName, measureScoreObj) {
    return measureName === ORF_ACC ? measureScoreObj.rawScore / 100 : measureScoreObj.rawScore;
}

/**
 * Group information about student scores by grade and period.
 * @param {Object} studentResults - Assessment result for a single student
 * @returns {Array} List of measure scores objects.
 */
function getScoresInfoByWindow(studentResults) {
    if (!Array.isArray(studentResults)) {
        throw Error('Results array is expected as function\'s argument');
    }
    return studentResults.reduce((measureScoresGroupByWindow, result) => {
        const {
            gradeLevel: grade,
            period,
            isValid,
            dataSource,
            fieldStudy,
        } = result;
        if (isValid && SUPPORTED_MEASURES.includes(result.measure) && !fieldStudy) {
            const scoreType = SECURE_ASSESSMENT_DATA_SOURCES.includes(dataSource)
                ? SECURE_SCORE_TYPE
                : REGULAR_SCORE_TYPE;
            return {
                ...measureScoresGroupByWindow,
                [grade]: {
                    ...(measureScoresGroupByWindow[grade]),
                    [period]: {
                        ...(measureScoresGroupByWindow[grade]?.[period] || {}),
                        [scoreType]: {
                            ...((measureScoresGroupByWindow[grade]?.[period] || {})[scoreType] || {}),
                            ...getScoresInfo(result),
                        },
                    },
                },
            };
        }
        if (!isValid) {
            console.warn(`Invalidated result ${result.resultId} cannot be used`);
        }
        return measureScoresGroupByWindow;
    }, {});
}

/**
 * Iterates over measure scores and return attemptNumber that is the same for all results.
 * Return null in case any result has no attemptNumber or has different value from others
 * @param {Object} scores - Assessment result for a single student in a single period {measure_name: {scores object}}
 * @returns {int} numeric value of attempt.
 */
const getAttemptNumber = (scores) => {
    const result = Object.values(scores).reduce((commonValue, currentObject) => {
        const currentAttempt = currentObject.attemptNumber;
        return commonValue === undefined || currentAttempt === commonValue ? currentAttempt : null;
    }, undefined);

    return result ?? null;
};


module.exports = {
    getAttemptNumber,
    getScoresInfoByWindow,
    getRawScore,
    has,
};
